import { useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";

import { captureException } from "common/utils/monitoring";
import { deepCombineErrors } from "common/utils/errors";
import { toDate } from "common/utils/dates";
import { SubscriptionPriceSummary } from "common/types/priceSummary";
import { PaymentMethodOption, UISubscriptionStatus } from "common/types";

import DrawerLayout from "client/common/components/stackables/DrawerLayout";
import InlineSelect from "client/common/components/inputs/InlineSelect";
import { useClientQuery } from "client/accounts/hooks";
import PaymentMethodSelectPanel from "client/payments/components/PaymentMethodSelectPanel";
import {
  getWalletPaymentItems,
  isPaymentOptionChargeable,
  UNCHARGEABLE_PAYMENT_METHOD_MESSAGE,
  walletDisplayText,
} from "client/payments/utils";
import {
  useSubscriptionQuery,
  useUpdateSubscriptionPaymentMethodMutation,
} from "client/subscriptions/hooks";
import { useCurrentDrawer } from "client/common/hooks/stackables";
import { usePaymentCreateDrawer } from "client/payments/stackables";
import CardPaymentDeclineAlert from "client/common/components/content/CardPaymentDeclineAlert";
import {
  useCreatePaymentMethodMutation,
  useWalletPaymentMethodRequest,
} from "client/payments/hooks";
import { useFeatures } from "client/clinics/hooks";

import { DetailSubscription, SubscriptionPaymentUpdate } from "../../types";
import { isSubscriptionAddressUnshippable } from "../../utils";
import SubscriptionAlert from "../SubscriptionAlert";

import styles from "../../styles/SubscriptionPaymentUpdateDrawer.module.less";

export type SubscriptionPaymentUpdateDrawerProps = {
  id: number;
};

type SubscriptionPaymentUpdateFormProps = {
  subscription: DetailSubscription;
};

type SubscriptionSchedulingType = "immediate" | "next";

const SubscriptionPaymentUpdateForm = ({ subscription }: SubscriptionPaymentUpdateFormProps) => {
  const { close } = useCurrentDrawer();
  const { open: openPaymentCreateDrawer } = usePaymentCreateDrawer();
  const { data: client, isLoading: isClientLoading, error: clientError } = useClientQuery();
  const isEdbEnabled = useFeatures().AUTOSHIP_EDB;
  const isFailedPaymentRecovery =
    subscription.visible_status === UISubscriptionStatus.FailedPayment;
  const [subscriptionSchedulingType, setSubscriptionSchedulingType] =
    useState<SubscriptionSchedulingType>("immediate");

  const [paymentMethod, setPaymentMethod] = useState<PaymentMethodOption | null>(
    subscription.payment_method ?? null,
  );

  const stripe = useStripe();
  const {
    mutate: updateSubscriptionPayment,
    mutateAsync: updateSubscriptionPaymentAsync,
    isPending: isUpdatingPaymentMethod,
    error: paymentUpdateError,
    reset: resetPaymentUpdate,
  } = useUpdateSubscriptionPaymentMethodMutation(subscription.id, {
    onSuccess: close,
    onError: (error, data) => {
      if (!stripe || !error.data?.stripe_action_client_secret) {
        return;
      }
      stripe.handleCardAction(error.data?.stripe_action_client_secret).then((result) => {
        if (result.paymentIntent) {
          updateSubscriptionPayment(data);
        }
      });
    },
  });

  const buildPaymentUpdate = (paymentMethodId: number): SubscriptionPaymentUpdate => ({
    payment_method_id: paymentMethodId,
    ...(isFailedPaymentRecovery &&
      subscriptionSchedulingType === "immediate" && { place_order_now: true }),
  });

  const {
    mutateAsync: createWalletPaymentMethod,
    isPending: isCreatingWalletPaymentMethod,
    error: createWalletPaymentMethodError,
  } = useCreatePaymentMethodMutation();

  const {
    request: walletPaymentRequest,
    supportedWallets,
    isLoading: isLoadingWalletPaymentRequest,
  } = useWalletPaymentMethodRequest({
    label: `Subscription ${subscription.display_number}`,
    amount: subscription.next_order_data.total,
    paymentItems: getWalletPaymentItems<SubscriptionPriceSummary>({
      ...subscription.next_order_data,
      tax: null,
      is_tax_final: false,
    }),
    onCreate: async (id) => {
      resetPaymentUpdate();
      const method = await createWalletPaymentMethod({ payment_method: id });
      await updateSubscriptionPaymentAsync(buildPaymentUpdate(method.id));
    },
  });

  const isSubmitting = isCreatingWalletPaymentMethod || isUpdatingPaymentMethod;

  if (isClientLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!client || clientError) {
    return <DrawerLayout.FetchError entityName="payment method" error={clientError} />;
  }

  const selectedPaymentMethod = paymentMethod ?? subscription.payment_method;
  const selectedPaymentIsUnchargeable =
    !!selectedPaymentMethod && !isPaymentOptionChargeable(selectedPaymentMethod);

  const canSubmit = !!(
    paymentMethod &&
    !selectedPaymentIsUnchargeable &&
    !isCreatingWalletPaymentMethod &&
    !isUpdatingPaymentMethod
  );

  const handleSubmit = () => {
    if (!paymentMethod) {
      return;
    }

    if (paymentMethod.id === "__wallet__") {
      walletPaymentRequest?.show();
      return;
    }

    if (typeof paymentMethod.id === "number") {
      updateSubscriptionPayment(buildPaymentUpdate(paymentMethod.id));
      return;
    }

    captureException(
      new Error(
        `Unexpected placeholder payment method [${JSON.stringify(paymentMethod)}] in SubscriptionPaymentUpdateDrawer`,
      ),
    );
  };

  const getSubmitText = () => {
    const authorizeLabel =
      paymentMethod?.id === "__wallet__"
        ? `Authorize ${walletDisplayText(paymentMethod.wallet)} payment`
        : "Authorize payment";
    if (isFailedPaymentRecovery && subscriptionSchedulingType === "immediate") {
      return `${authorizeLabel} & place order`;
    }
    return authorizeLabel;
  };

  const getScheduleHelpText = () => {
    const paymentMethodLabel =
      paymentMethod?.id === "__wallet__"
        ? `${walletDisplayText(paymentMethod.wallet)} payment`
        : "card";
    if (subscriptionSchedulingType === "immediate") {
      return `We'll place your order today with this ${paymentMethodLabel}.`;
    }
    if (isEdbEnabled && subscription.expected_delivery_by) {
      return `We'll use this ${paymentMethodLabel} on your next scheduled order - estimated delivery ${toDate(
        subscription.expected_delivery_by,
      )}.`;
    }
    return `We'll use this ${paymentMethodLabel} on your next scheduled order.`;
  };

  const handlePaymentMethodChange = (method: PaymentMethodOption) => {
    if (method.id !== paymentMethod?.id) {
      resetPaymentUpdate();
    }
    setPaymentMethod(method);
  };

  const formError = createWalletPaymentMethodError || paymentUpdateError;
  const cardDeclineError = paymentUpdateError?.data?.card_decline_info;

  return (
    <DrawerLayout>
      <DrawerLayout.Header
        title="Update payment method"
        description="This will update the payment method used to pay for this Autoship. All other Autoships will not be affected. Your card may be pre-authorized with a temporary $1.00 hold that will be removed within 7 business days."
        onClose={close}
      />
      <DrawerLayout.Form
        submitText={getSubmitText()}
        canSubmit={canSubmit}
        submitTooltipContent={
          selectedPaymentIsUnchargeable ? UNCHARGEABLE_PAYMENT_METHOD_MESSAGE : undefined
        }
        onSubmit={handleSubmit}
        error={cardDeclineError ? null : formError}
        fieldErrors={[deepCombineErrors(formError?.errors)]}
        isLoading={isSubmitting}
      >
        {isSubscriptionAddressUnshippable(subscription) && (
          <DrawerLayout.Alerts>
            <SubscriptionAlert.Address subscription={subscription} />
          </DrawerLayout.Alerts>
        )}
        {cardDeclineError && <CardPaymentDeclineAlert declineError={cardDeclineError} />}
        <PaymentMethodSelectPanel
          initialValue={subscription.payment_method}
          value={paymentMethod ?? subscription.payment_method}
          options={client.payment_methods}
          walletPayment={supportedWallets}
          isLoading={isLoadingWalletPaymentRequest}
          disabled={isSubmitting}
          onChange={handlePaymentMethodChange}
          onAdd={() =>
            openPaymentCreateDrawer({
              onSuccess: handlePaymentMethodChange,
            })
          }
        />
        {isFailedPaymentRecovery && (
          <div className={styles.orderDateSection}>
            <DrawerLayout.Form.Group>
              <DrawerLayout.Form.Label>
                {isEdbEnabled ? "Delivery date" : "Order date"}
              </DrawerLayout.Form.Label>
              <InlineSelect<SubscriptionSchedulingType>
                value={subscriptionSchedulingType}
                onChange={setSubscriptionSchedulingType}
                disabled={isSubmitting}
              >
                <InlineSelect.Option value="immediate">
                  {isEdbEnabled ? "Soonest" : "Immediate"}
                </InlineSelect.Option>
                <InlineSelect.Option value="next">
                  {isEdbEnabled ? "Next delivery" : "Next order"}
                </InlineSelect.Option>
              </InlineSelect>
              <div className={styles.helpText}>{getScheduleHelpText()}</div>
            </DrawerLayout.Form.Group>
          </div>
        )}
      </DrawerLayout.Form>
    </DrawerLayout>
  );
};

const SubscriptionPaymentUpdateDrawer = ({ id }: SubscriptionPaymentUpdateDrawerProps) => {
  const { data: subscription, isLoading, error } = useSubscriptionQuery(id);

  if (isLoading) {
    return <DrawerLayout.Loading />;
  }

  if (!subscription || error) {
    return <DrawerLayout.FetchError entityName="Autoship" error={error} />;
  }

  return <SubscriptionPaymentUpdateForm subscription={subscription} />;
};

export default SubscriptionPaymentUpdateDrawer;
